/**
 * Crane Cost Estimator — public calculator page (PRP-CALC-1 S3, S8).
 *
 * Sizes a mobile crane for a restoration lift, estimates rental + labor cost
 * from published public rate schedules, and produces a justification narrative
 * an estimator can paste into a supplement. Carries the /tools SEO treatment:
 * per-route meta + JSON-LD, scenario-coverage section, app promo band.
 */
import { useState } from 'react';

import { Button } from '../../components/ui/Button';
import ToolPageShell, { DimensionDivider, Eyebrow } from './ToolPageShell';
import AppPromo from './AppPromo';
import CalcResults from './CalcResults';
import ScenarioInterview from './ScenarioInterview';
import {
  MethodSection,
  FaqSection,
  SourcesSection,
  AdvancedRegional,
  NumberField,
  SelectField,
  CheckboxField,
  type FaqItem,
} from './_shared';
import { useSeo, faqJsonLd, calculatorJsonLd } from './useSeo';
import { CalcAPI, type CalcResponse, type CraneSetupSurface } from './calcApi';

/** The 12 jobs the scenario mode knows how to derive, phrased the way people
 * search. Rendered as on-page content (SEO) and honest capability list. */
const COVERED_JOBS: { name: string; detail: string }[] = [
  {
    name: 'Remove and reset rooftop units for a roof replacement',
    detail:
      'Two visits — lift the RTUs off before tear-off, set them back after the new roof is in.',
  },
  {
    name: 'Swap a rooftop HVAC unit',
    detail:
      'Old unit off, new unit on, one mobilization. Weight comes from the tonnage if you do not have the data plate.',
  },
  {
    name: 'Set new rooftop equipment',
    detail: 'One-time set of a packaged RTU, exhaust fan, or curb package.',
  },
  {
    name: 'Set condensers and chillers',
    detail:
      'Ground or roof set, sized from cited manufacturer weight bands by tonnage.',
  },
  {
    name: 'Load roofing materials to the roof',
    detail:
      'TPO rolls, insulation, pallets. Recommends a telehandler instead when the load fits one.',
  },
  {
    name: 'Set structural steel and beams',
    detail:
      'Reads the member weight straight from the AISC designation (a W24x76 weighs 76 lb per foot).',
  },
  {
    name: 'Set trusses and framing packages',
    detail:
      'Individual picks or banded bundles — pick count usually governs the crane day, not capacity.',
  },
  {
    name: 'Set skylights and roof curbs',
    detail: 'Light picks; a 30-ton boom truck usually covers it.',
  },
  {
    name: 'Set a generator or padmount transformer',
    detail: 'Weight bands by kW and kVA from manufacturer spec sheets.',
  },
  {
    name: 'Remove tree sections over a house',
    detail:
      'The classic storm claim. Section weights from green-log charts by species, with the arborist rigging cycle priced in.',
  },
  {
    name: 'Set a modular building, shed, or spa',
    detail:
      'Over-the-house sets included — radius across the structure plus clearance over the ridge.',
  },
  {
    name: 'Set signage and antennas',
    detail:
      'Height governs, not weight. Includes the wind and sail-area warning your rigger will give you anyway.',
  },
];

const FAQ_ITEMS: FaqItem[] = [
  {
    q: 'How much does a crane cost for an insurance claim?',
    a: 'Most residential restoration picks land on a 30 to 50 ton crane at roughly $175 to $250 per hour with an operator, against a 4-hour minimum per visit, plus mobilization each way. A one-visit job typically runs $1,000 to $3,500; two mobilizations (remove and reset) roughly double the mobilization and minimum charges. The calculator prices your specific pick from published rate schedules and shows the market band next to the ownership-cost floor.',
  },
  {
    q: 'Why does the crane size jump when I increase the radius?',
    a: 'Rated capacity drops with distance. A crane that lifts a load easily at 20 feet may be under the 85 percent margin at 45 feet, so the tool steps up to a larger machine to keep the pick safe.',
  },
  {
    q: 'What is the 4-hour minimum?',
    a: 'Operated crane rental is billed portal to portal against a minimum, almost always 4 hours. A 30-minute pick still pays the minimum, which is why combining picks into one visit matters more than shaving the pick time.',
  },
  {
    q: 'Do I need a signal person?',
    a: 'OSHA requires a qualified signal person whenever the operator cannot see the load or the load path, and in most power-line and blind-pick situations. The tool flags this and includes the signal-person time when the site conditions call for it.',
  },
  {
    q: 'When does a lift need an engineered lift plan?',
    a: 'When the pick is critical or runs over 75 percent of chart capacity, the crane provider should prepare an engineered lift plan. The calculator warns you when your numbers cross that line. Its output is reference sizing for estimating, not a lift plan.',
  },
  {
    q: 'Can I use this number in an Xactimate supplement?',
    a: 'Yes — that is what it is for. The result includes the crane class, the sizing rationale, the rate source, and a narrative you can paste into the supplement. Where a matching Xactimate equipment item exists (EQU CRANE, BTRUK), the tool names it so the line prices regionally at import.',
  },
  {
    q: 'Is this a quote?',
    a: 'No. It is a planning estimate built from published public rate schedules. Confirm the crane, access, and price with a rental yard or lift director before you rely on it.',
  },
  {
    q: 'What if my lift is bigger than the tool supports?',
    a: 'If the pick is beyond the tool range you will get a written recommendation to have a lift plan prepared by a crane provider instead of a line-item table.',
  },
];

export default function CraneCalculatorPage() {
  useSeo({
    title:
      'Crane Cost Estimator for Insurance Claims — Free Crane Sizing Calculator | Antero Trail',
    description:
      'Free crane cost calculator for restoration and insurance claims. Describe the job — RTU swap, tree removal over a house, steel, trusses, generators — and get a sized crane class, day count, cost range from published rate schedules, and a justification narrative for your supplement.',
    path: '/tools/crane-cost-estimator',
    jsonLd: [
      calculatorJsonLd({
        name: 'Crane Cost Estimator for Insurance Claims',
        description:
          'Sizes a mobile crane from the pick weight, radius, and tip height, prices it from published public rate schedules, and writes a justification narrative for restoration supplements.',
        path: '/tools/crane-cost-estimator',
      }),
      faqJsonLd(FAQ_ITEMS),
    ],
  });

  const [mode, setMode] = useState<'scenario' | 'advanced'>('scenario');
  const [pickWeight, setPickWeight] = useState<number>(4000);
  const [pickRadius, setPickRadius] = useState<number>(40);
  const [tipHeight, setTipHeight] = useState<number>(35);
  const [pickCount, setPickCount] = useState<number>(6);
  const [surface, setSurface] = useState<CraneSetupSurface>('lot');
  const [powerLines, setPowerLines] = useState(false);
  const [occupied, setOccupied] = useState(false);
  const [picksPerDay, setPicksPerDay] = useState<number | ''>('');
  const [regional, setRegional] = useState(0);

  const [result, setResult] = useState<CalcResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await CalcAPI.crane(
        {
          pick_weight_lb: pickWeight,
          pick_radius_ft: pickRadius,
          tip_height_ft: tipHeight,
          pick_count: pickCount,
          setup_surface: surface,
          power_lines_within_20ft: powerLines,
          occupied_building: occupied,
          ...(picksPerDay !== '' ? { picks_per_day_override: picksPerDay } : {}),
        },
        regional,
      );
      setResult(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
      setResult(null);
    } finally {
      setLoading(false);
    }
  }

  return (
    <ToolPageShell>
      <Eyebrow>Lifting &amp; access — free calculator</Eyebrow>
      <h1 className="mt-2 font-ui text-4xl font-bold tracking-tight sm:text-[2.75rem] sm:leading-tight">
        Crane Cost Estimator for Insurance Claims
      </h1>
      <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-muted-foreground">
        Size a mobile crane for a restoration lift and estimate the rental and
        crew cost from published public rate schedules. Describe the job in
        plain terms — an RTU swap, a tree over the house, a truss package —
        and the tool derives the pick weight, radius, and tip height for you.
        Or enter the engineering numbers directly.
      </p>

      {/* Mode toggle: describe the job (S8 scenario) vs raw parameters */}
      <div className="mt-8 flex flex-wrap gap-2" role="tablist" aria-label="Input mode">
        <Button
          type="button"
          role="tab"
          aria-selected={mode === 'scenario'}
          variant={mode === 'scenario' ? 'primary' : 'outline'}
          onClick={() => setMode('scenario')}
        >
          Describe the job
        </Button>
        <Button
          type="button"
          role="tab"
          aria-selected={mode === 'advanced'}
          variant={mode === 'advanced' ? 'primary' : 'outline'}
          onClick={() => setMode('advanced')}
        >
          Advanced mode (engineering inputs)
        </Button>
      </div>

      {mode === 'scenario' && (
        <div className="mt-6 max-w-3xl rounded-lg border border-border bg-card p-5 sm:p-6">
          <ScenarioInterview calculator="crane" />
        </div>
      )}

      <div className={mode === 'advanced' ? 'mt-8 grid gap-8 lg:grid-cols-2' : 'hidden'}>
        {/* Form */}
        <form
          onSubmit={onSubmit}
          className="space-y-4 rounded-lg border border-border bg-card p-5 sm:p-6"
          aria-label="Crane inputs"
        >
          <NumberField
            label="Heaviest pick weight"
            suffix="lb"
            value={pickWeight}
            min={1}
            onChange={setPickWeight}
            hint="Weight of the single heaviest item the crane must lift."
          />
          <NumberField
            label="Pick radius"
            suffix="ft"
            value={pickRadius}
            min={1}
            onChange={setPickRadius}
            hint="Horizontal distance from the crane center to where the load lands."
          />
          <NumberField
            label="Tip height"
            suffix="ft"
            value={tipHeight}
            min={1}
            onChange={setTipHeight}
            hint="Height the load must clear at the top of the lift."
          />
          <NumberField
            label="Number of picks"
            value={pickCount}
            min={1}
            step="1"
            onChange={setPickCount}
          />
          <SelectField<CraneSetupSurface>
            label="Setup surface"
            value={surface}
            onChange={setSurface}
            options={[
              { value: 'street', label: 'Paved street' },
              { value: 'lot', label: 'Parking lot / firm ground' },
              { value: 'soil', label: 'Soil / soft ground' },
            ]}
          />
          <div className="space-y-2 pt-1">
            <CheckboxField
              label="Power lines within 20 ft"
              checked={powerLines}
              onChange={setPowerLines}
            />
            <CheckboxField
              label="Occupied building nearby"
              checked={occupied}
              onChange={setOccupied}
            />
          </div>
          <NumberField
            label="Picks per day override"
            value={picksPerDay}
            min={1}
            step="1"
            onChange={(v) => setPicksPerDay(Number.isNaN(v) ? '' : v)}
            hint="Optional. Leave blank to let the tool estimate daily throughput."
          />

          <AdvancedRegional value={regional} onChange={setRegional} />

          <Button type="submit" isLoading={loading} className="w-full">
            Estimate crane cost
          </Button>
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
        </form>

        {/* Results */}
        <div>
          {result ? (
            <CalcResults result={result} />
          ) : (
            <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
              Enter your lift details and select Estimate crane cost to see a
              sized crane, cost range, and a justification narrative.
            </div>
          )}
        </div>
      </div>

      <section className="mt-14">
        <DimensionDivider label="What it covers" />
        <h2 className="mb-3 mt-5 font-ui text-2xl font-bold tracking-tight">
          Twelve crane jobs it knows how to size
        </h2>
        <p className="mb-5 max-w-3xl text-[15px] leading-relaxed text-muted-foreground">
          "Describe the job" mode asks the questions an estimator can actually
          answer — what is being lifted, how far the crane sits from the
          building, how high the roof is — and derives the engineering inputs
          with the formula and source shown for every derived number. It
          currently covers:
        </p>
        <ul className="grid max-w-4xl gap-x-8 gap-y-3 sm:grid-cols-2">
          {COVERED_JOBS.map((job) => (
            <li key={job.name} className="flex gap-3">
              <span
                aria-hidden
                className="mt-2 h-2.5 w-px shrink-0 bg-amber-500"
              />
              <span className="text-sm leading-relaxed">
                <span className="font-semibold text-foreground">
                  {job.name}.
                </span>{' '}
                <span className="text-muted-foreground">{job.detail}</span>
              </span>
            </li>
          ))}
        </ul>
      </section>

      <AppPromo
        className="mt-14"
        context="In the app, a crane scenario lands in your estimate as priced Xactimate line items (EQU CRANE, BTRUK, EQU DLVR) with the sizing narrative attached — no copy-paste."
      />

      <MethodSection>
        <p>
          The tool sizes the crane from the load chart, not the bare weight. A
          crane's rated capacity falls as the load moves away from the machine,
          so the pick radius and tip height matter as much as the weight. The
          standard practice is to keep the heaviest pick at or under 85 percent
          of the crane's rated capacity at that radius. That 15 percent margin
          absorbs rigging weight, wind, and the swing of the load, and it is the
          margin most rental yards and lift directors plan around.
        </p>
        <p>
          Cost has two parts. The first is the crane and operator, priced by the
          day or half day from published equipment rate schedules. The second is
          the supporting crew and setup time, which grows when the site is
          harder to work: soft ground needs mats and cribbing, and power lines
          within 20 feet trigger the extra spotting and clearance procedures
          that a signal person is required to manage. The tool adds those site
          factors as separate line items so you can see what is driving the
          number, then rolls everything into a low to high range rather than a
          single false-precision figure.
        </p>
        <p>
          Two price layers are shown and never blended: the market band (what
          operated crane services actually charge, from published rate sheets)
          headlines the result, and the ownership-and-operating cost floor from
          government schedules sits underneath as the justification anchor.
        </p>
      </MethodSection>

      <FaqSection items={FAQ_ITEMS} />

      <SourcesSection
        sources={[
          {
            name: 'Caltrans Labor Surcharge and Equipment Rental Rates',
            detail:
              'Published equipment rental rate schedule used as the public basis for crane day and half-day rates.',
          },
          {
            name: 'USACE EP 1110-1-8 equipment rate references (Region III)',
            detail:
              'Federal equipment ownership and operating cost references used as the cost floor.',
          },
          {
            name: 'Published operated-crane rate sheets',
            detail:
              'Market rental band for the headline "expect to pay" range, cited with source and date in the result.',
          },
          {
            name: 'OSHA 1926 Subpart CC, Cranes and Derricks in Construction',
            detail:
              'Signal person, power-line clearance, and rated-capacity requirements that drive the site factors and the 85 percent margin.',
          },
          {
            name: 'Manufacturer load charts and spec sheets',
            detail:
              'Grove, Link-Belt, and National Crane load charts for capacity classes; manufacturer weight tables (AISC, SBCA, Generac, Kohler, and others) for scenario weight bands — each cited on the derived value that uses it.',
          },
        ]}
      />
    </ToolPageShell>
  );
}
